import { parsePackageCount, parsePackageCountMatch } from "@/lib/pricing/variant-pack-math";

export interface PurchasableVariantCandidate {
	id: string;
	name: string;
	quantityAvailable?: number | null;
	pricing?: {
		price?: {
			gross?: {
				amount?: number | null;
			} | null;
		} | null;
	} | null;
	selectionAttributes?:
		| readonly {
				attribute?: { slug?: string | null } | null;
				values?: readonly { name?: string | null; value?: string | null }[] | null;
		  }[]
		| null;
}

export type VariantResolutionSource =
	"requested" | "requested-dose-fallback" | "auto-stock" | "display-fallback" | "none";

export interface VariantResolution<T extends PurchasableVariantCandidate> {
	variant: T | null;
	source: VariantResolutionSource;
	requestedVariant: T | null;
	isPurchasable: boolean;
}

export interface PurchasableVariantDetails {
	doseLabel: string | null;
	doseKey: string | null;
	packCount: number;
}

const DOSAGE_ATTRIBUTE_SLUGS = new Set([
	"dosage",
	"dosierung",
	"dosis",
	"dosaggio",
	"dosering",
	"dosagem",
	"dawka",
	"strength",
]);

const PACKAGE_ATTRIBUTE_SLUGS = new Set([
	"package",
	"packaging",
	"pack",
	"bundle",
	"quantity",
	"amount",
	"gebinde",
	"menge",
	"anzahl",
	"packung",
	"verpackung",
	"cantidad",
	"quantite",
	"quantita",
	"hoeveelheid",
	"aantal",
	"quantidade",
	"ilosc",
]);

function normalizeAttributeSlug(slug: string): string {
	return slug
		.normalize("NFKD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();
}

function getAttributeValue(variant: PurchasableVariantCandidate, slugs: ReadonlySet<string>): string | null {
	for (const assigned of variant.selectionAttributes ?? []) {
		const slug = normalizeAttributeSlug(assigned.attribute?.slug ?? "");
		if (!slugs.has(slug)) continue;
		const value = assigned.values?.[0];
		const label = value?.name?.trim() || value?.value?.trim();
		if (label) return label;
	}
	return null;
}

function normalizeDoseLabel(value: string): string {
	const match = value.match(/(\d+(?:[.,]\d+)?)\s*(mg|µg|ug|mcg|ml|iu)\b/i);
	if (!match) return value.trim().replace(/\s+/g, " ");
	const amount = match[1]!.replace(",", ".");
	const unit = match[2]!.toLowerCase();
	if (unit === "iu") return `${amount} IU`;
	const normalizedUnit = unit === "ug" || unit === "mcg" || unit === "µg" ? "µg" : unit;
	return `${amount}${normalizedUnit}`;
}

function getDoseLabel(variant: PurchasableVariantCandidate): string | null {
	const attributeValue = getAttributeValue(variant, DOSAGE_ATTRIBUTE_SLUGS);
	if (attributeValue) return normalizeDoseLabel(attributeValue);

	const match = variant.name.match(/\d+(?:[.,]\d+)?\s*(?:mg|µg|ug|mcg|ml|iu)\b/i);
	return match ? normalizeDoseLabel(match[0]) : null;
}

function getPackCount(variant: PurchasableVariantCandidate): number {
	for (const assigned of variant.selectionAttributes ?? []) {
		const slug = normalizeAttributeSlug(assigned.attribute?.slug ?? "");
		if (!PACKAGE_ATTRIBUTE_SLUGS.has(slug)) continue;
		const value = assigned.values?.[0];
		const raw = value?.value?.trim() ?? "";
		const numericRaw = raw.match(/^\s*(\d+)\s*$/)?.[1];
		if (numericRaw) return Number.parseInt(numericRaw, 10);
		const rawMatch = raw ? parsePackageCountMatch(raw) : null;
		if (rawMatch !== null) return rawMatch;
		const labelMatch = value?.name ? parsePackageCountMatch(value.name) : null;
		if (labelMatch !== null) return labelMatch;
		// An unrecognised localized label is not proof of a one-pack. Fall through
		// to the stable variant-name parser instead.
		break;
	}
	return parsePackageCount(variant.name);
}

function normalizeDoseKey(doseLabel: string | null): string | null {
	if (!doseLabel) return null;
	return doseLabel.toLowerCase().replace(/\s+/g, "");
}

function getDoseSortKey(doseLabel: string | null): { dimension: number; amount: number } {
	if (!doseLabel) return { dimension: Number.POSITIVE_INFINITY, amount: Number.POSITIVE_INFINITY };
	const match = doseLabel.match(/(\d+(?:[.,]\d+)?)\s*(mg|µg|ug|mcg|ml|iu)?/i);
	if (!match) return { dimension: Number.POSITIVE_INFINITY, amount: Number.POSITIVE_INFINITY };

	const amount = Number.parseFloat(match[1]!.replace(",", "."));
	const unit = match[2]?.toLowerCase();
	if (!Number.isFinite(amount)) {
		return { dimension: Number.POSITIVE_INFINITY, amount: Number.POSITIVE_INFINITY };
	}

	if (unit === "mg") return { dimension: 0, amount };
	if (unit === "µg" || unit === "ug" || unit === "mcg") return { dimension: 0, amount: amount / 1000 };
	if (unit === "ml") return { dimension: 1, amount };
	if (unit === "iu") return { dimension: 2, amount };
	return { dimension: 3, amount };
}

export function getPurchasableVariantDetails(
	variant: PurchasableVariantCandidate,
): PurchasableVariantDetails {
	const doseLabel = getDoseLabel(variant);
	return {
		doseLabel,
		doseKey: normalizeDoseKey(doseLabel),
		packCount: Math.max(1, getPackCount(variant)),
	};
}

/**
 * Saleor uses `null` quantity for variants whose inventory is not tracked. Only
 * an explicit zero is out of stock. A real, finite, non-negative channel price
 * is also required before a variant can be purchased (zero-price variants are valid).
 */
export function isVariantPurchasable(variant: PurchasableVariantCandidate): boolean {
	const amount = variant.pricing?.price?.gross?.amount;
	return (
		variant.quantityAvailable !== 0 && typeof amount === "number" && Number.isFinite(amount) && amount >= 0
	);
}

function compareVariants<T extends PurchasableVariantCandidate>(
	a: { variant: T; index: number; details: PurchasableVariantDetails },
	b: { variant: T; index: number; details: PurchasableVariantDetails },
): number {
	const aDose = getDoseSortKey(a.details.doseLabel);
	const bDose = getDoseSortKey(b.details.doseLabel);
	return (
		aDose.dimension - bDose.dimension ||
		aDose.amount - bDose.amount ||
		a.details.packCount - b.details.packCount ||
		a.index - b.index
	);
}

/**
 * Resolve one stable PDP variant for every surface (gallery, buy box, schema and tracking).
 * Explicit purchasable selections win. An unavailable explicit selection first
 * falls back within the same dose, then to the smallest purchasable dose/package.
 */
export function resolvePurchasableVariant<T extends PurchasableVariantCandidate>(
	variants: readonly T[],
	requestedVariantId?: string | null,
): VariantResolution<T> {
	if (variants.length === 0) {
		return { variant: null, source: "none", requestedVariant: null, isPurchasable: false };
	}

	const candidates = variants.map((variant, index) => ({
		variant,
		index,
		details: getPurchasableVariantDetails(variant),
	}));
	const requested = requestedVariantId
		? (candidates.find(({ variant }) => variant.id === requestedVariantId) ?? null)
		: null;

	if (requested && isVariantPurchasable(requested.variant)) {
		return {
			variant: requested.variant,
			source: "requested",
			requestedVariant: requested.variant,
			isPurchasable: true,
		};
	}

	const purchasable = candidates.filter(({ variant }) => isVariantPurchasable(variant));
	if (requested?.details.doseKey) {
		const sameDose = purchasable
			.filter(({ details }) => details.doseKey === requested.details.doseKey)
			.sort(compareVariants)[0];
		if (sameDose) {
			return {
				variant: sameDose.variant,
				source: "requested-dose-fallback",
				requestedVariant: requested.variant,
				isPurchasable: true,
			};
		}
	}

	const automatic = purchasable.sort(compareVariants)[0];
	if (automatic) {
		return {
			variant: automatic.variant,
			source: "auto-stock",
			requestedVariant: requested?.variant ?? null,
			isPurchasable: true,
		};
	}

	// When everything is unavailable, preserve a valid explicit deep-link for
	// transparency. Otherwise display the same deterministic dose/package that
	// would become the default once stock returns.
	const displayFallback = requested ?? candidates.sort(compareVariants)[0]!;
	return {
		variant: displayFallback.variant,
		source: "display-fallback",
		requestedVariant: requested?.variant ?? null,
		isPurchasable: false,
	};
}
